import Phaser from "phaser";
import { TILE, worldToScreen, type ScreenPos } from "./Iso";

const ELEV_PX = 6;

/* ───── texture keys ───── */
export const FLOWER_RED_KEY = "iso-flower-red";
export const FLOWER_YELLOW_KEY = "iso-flower-yellow";
export const FLOWER_BLUE_KEY = "iso-flower-blue";
export const SIGNPOST_TEXTURE_KEY = "iso-signpost";
export const TRAIL_STONE_KEY = "iso-trail-stone";
export const BUSH_TEXTURE_KEY = "iso-bush";
export const WELL_TEXTURE_KEY = "iso-well";

/* ───── texture generators ───── */

export function ensurePropTextures(scene: Phaser.Scene): void {
  generateFlowerTexture(scene, FLOWER_RED_KEY, 0xe84040, 0xf06060);
  generateFlowerTexture(scene, FLOWER_YELLOW_KEY, 0xf0c020, 0xf5d850);
  generateFlowerTexture(scene, FLOWER_BLUE_KEY, 0x4080e0, 0x60a0f0);
  generateSignpostTexture(scene);
  generateTrailStoneTexture(scene);
  generateBushTexture(scene);
  generateWellTexture(scene);
}

function generateFlowerTexture(scene: Phaser.Scene, key: string, petalColor: number, centerColor: number): void {
  if (scene.textures.exists(key)) return;
  const W = 14;
  const H = 18;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Stem
  g.lineStyle(1, 0x3a7a3a, 1);
  g.lineBetween(7, 10, 7, 17);

  // Leaf
  g.fillStyle(0x4a8a4a, 1);
  g.fillEllipse(10, 14, 5, 3);

  // Petals (5 small circles around center)
  g.fillStyle(petalColor, 1);
  const cx = 7;
  const cy = 7;
  const pr = 2.5;
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
    g.fillCircle(cx + Math.cos(angle) * 3, cy + Math.sin(angle) * 3, pr);
  }

  // Center
  g.fillStyle(centerColor, 1);
  g.fillCircle(cx, cy, 2);

  g.generateTexture(key, W, H);
  g.destroy();
}

function generateSignpostTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(SIGNPOST_TEXTURE_KEY)) return;
  const W = 24;
  const H = 38;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Post
  g.fillStyle(0x7a5a3a, 1);
  g.fillRect(11, 10, 3, 28);
  g.lineStyle(1, 0x5a3a20, 0.8);
  g.strokeRect(11, 10, 3, 28);

  // Sign board
  g.fillStyle(0x9a7a5a, 1);
  g.fillRect(1, 4, 22, 12);
  g.lineStyle(1, 0x5a3a20, 1);
  g.strokeRect(1, 4, 22, 12);

  // Text lines (decorative scratches)
  g.lineStyle(1, 0x5a3a20, 0.5);
  g.lineBetween(4, 8, 18, 8);
  g.lineBetween(4, 11, 14, 11);

  // Post top cap
  g.fillStyle(0x7a5a3a, 1);
  g.fillRect(10, 3, 5, 3);

  g.generateTexture(SIGNPOST_TEXTURE_KEY, W, H);
  g.destroy();
}

function generateTrailStoneTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TRAIL_STONE_KEY)) return;
  const W = 20;
  const H = 12;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Small flat stepping stones in iso perspective
  g.fillStyle(0x7a7a70, 0.7);
  g.fillEllipse(6, 5, 10, 6);

  g.fillStyle(0x8a8a80, 0.6);
  g.fillEllipse(14, 8, 8, 5);

  g.lineStyle(1, 0x5a5a50, 0.3);
  g.strokeEllipse(6, 5, 10, 6);
  g.strokeEllipse(14, 8, 8, 5);

  g.generateTexture(TRAIL_STONE_KEY, W, H);
  g.destroy();
}

function generateBushTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(BUSH_TEXTURE_KEY)) return;
  const W = 24;
  const H = 18;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Three overlapping circles for bushy foliage
  g.fillStyle(0x2a6a2a, 1);
  g.fillCircle(8, 12, 7);
  g.fillStyle(0x3a7a3a, 1);
  g.fillCircle(15, 10, 8);
  g.fillStyle(0x2e6e2e, 1);
  g.fillCircle(11, 8, 6);

  // Subtle outline
  g.lineStyle(1, 0x1a4a1a, 0.6);
  g.strokeCircle(8, 12, 7);
  g.strokeCircle(15, 10, 8);

  g.generateTexture(BUSH_TEXTURE_KEY, W, H);
  g.destroy();
}

function generateWellTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(WELL_TEXTURE_KEY)) return;
  const W = 28;
  const H = 34;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // Stone base (iso oval)
  g.fillStyle(0x8a8a80, 1);
  g.fillEllipse(14, 26, 24, 12);
  g.lineStyle(1, 0x5a5a50, 1);
  g.strokeEllipse(14, 26, 24, 12);

  // Inner hole (dark)
  g.fillStyle(0x1a1a2a, 0.8);
  g.fillEllipse(14, 24, 14, 7);

  // Stone wall rim
  g.lineStyle(2, 0x6a6a60, 1);
  g.strokeEllipse(14, 24, 18, 9);

  // Support posts
  g.fillStyle(0x6a4a2a, 1);
  g.fillRect(5, 8, 3, 18);
  g.fillRect(20, 8, 3, 18);

  // Roof beam
  g.fillStyle(0x7a5a3a, 1);
  g.fillRect(4, 6, 20, 3);

  // Rope
  g.lineStyle(1, 0x9a8a6a, 1);
  g.lineBetween(14, 9, 14, 20);

  // Bucket (tiny)
  g.fillStyle(0x6a4a2a, 1);
  g.fillRect(12, 18, 5, 4);

  g.generateTexture(WELL_TEXTURE_KEY, W, H);
  g.destroy();
}

/* ───── placement ───── */

interface PropPlacement {
  textureKey: string;
  positions: Array<[number, number]>;
  scale?: number;
  originY?: number;
}

const ZONE_DECORATIONS: PropPlacement[] = [
  // Sign posts
  {
    textureKey: SIGNPOST_TEXTURE_KEY,
    positions: [
      [6, 5],
      [9, 9],
      [3, 10],
    ],
  },
  {
    textureKey: FLOWER_RED_KEY,
    positions: [
      [5, 2],
      [8, 7],
      [14, 5],
      [3, 14],
      [11, 9],
    ],
    originY: 1,
  },
  {
    textureKey: FLOWER_YELLOW_KEY,
    positions: [
      [6, 3],
      [10, 6],
      [2, 11],
      [13, 8],
    ],
    originY: 1,
  },
  {
    textureKey: FLOWER_BLUE_KEY,
    positions: [
      [4, 8],
      [9, 3],
      [12, 10],
      [7, 13],
    ],
    originY: 1,
  },
  // Bushes
  {
    textureKey: BUSH_TEXTURE_KEY,
    positions: [
      [5, 4],
      [8, 12],
      [13, 6],
      [1, 7],
      [10, 2],
      [6, 11],
    ],
    originY: 1,
  },
  // Trail stones (leading between landmarks)
  {
    textureKey: TRAIL_STONE_KEY,
    positions: [
      [5, 5],
      [6, 6],
      [7, 7],
      [8, 8],
      [9, 8],
      [10, 9],
      [10, 10],
      [7, 5],
      [8, 5],
      [9, 5],
    ],
    originY: 0.5,
  },
  // Well
  {
    textureKey: WELL_TEXTURE_KEY,
    positions: [[7, 9]],
    originY: 1,
  },
];

export function placeZoneDecorations(
  scene: Phaser.Scene,
  origin: ScreenPos,
  heightMap: number[][],
  waterMap?: boolean[][],
): Phaser.GameObjects.Image[] {
  const images: Phaser.GameObjects.Image[] = [];

  for (const deco of ZONE_DECORATIONS) {
    for (const [tx, ty] of deco.positions) {
      if (waterMap?.[ty]?.[tx]) continue;
      const elev = heightMap[ty]?.[tx] ?? 0;
      const elevPx = elev * ELEV_PX;
      const { sx, sy } = worldToScreen(tx, ty);

      // Houses need extra downward offset so they sit on the tile surface
      const baseY = origin.sy + sy + TILE.height - elevPx;
      const yOffset = 0;
      const img = scene.add.image(
        origin.sx + sx + TILE.halfW,
        baseY + yOffset,
        deco.textureKey,
      );
      img.setOrigin(0.5, deco.originY ?? 1);
      img.setScale(deco.scale ?? 1);

      // Trail stones sit flat on the ground (lower depth layer)
      const depthOffset = deco.textureKey === TRAIL_STONE_KEY ? -1 : 1;
      img.setDepth((tx + ty) * 1000 + depthOffset);

      images.push(img);
    }
  }

  return images;
}
